
/**
 * Apply custom styling to the Google Gantt Chart
 * 
 * @param chartContainerRef - Reference to the chart container element
 * @param chartHeight - The calculated height of the chart
 */
export const applyChartStyles = (
  chartContainerRef: React.RefObject<HTMLDivElement>,
  chartHeight: number,
  selectedProject: any
): void => {
  // Add custom CSS for hover effects, whitespace removal, and improved tooltips
  const style = document.createElement('style');
  style.textContent = `
    .google-visualization-tooltip {
      background-color: #000000 !important;
      border: 1px solid #333 !important;
      border-radius: 6px !important;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4) !important;
      padding: 0 !important;
      color: white !important;
      font-family: Inter, system-ui, sans-serif !important;
      z-index: 10000 !important;
      pointer-events: none !important;
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
    }
    
    .google-visualization-tooltip-item-list {
      padding: 0 !important;
      margin: 0 !important;
    }
    
    .google-visualization-tooltip-item {
      margin: 5px 0 !important;
    }
    
    /* Add double-click hint on hover */
    .google-visualization-chart-table-row:hover {
      cursor: pointer;
    }
    
    /* Enhanced: Target and remove whitespace below dates more aggressively */
    .google-visualization-container svg > g > g:first-child {
      transform: translateY(-15px) !important;
    }
    
    /* Completely remove any margins/padding in the container */
    .google-visualization-container,
    .google-visualization-container svg {
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
      display: block !important;
    }
    
    /* Remove the specific whitespace area below dates */
    .google-visualization-container svg > g > g:nth-child(2) > g:first-child {
      display: none !important; 
    }
    
    /* Compress timeline date elements */
    .google-visualization-container text[text-anchor="middle"] {
      font-size: 11px !important;
      alignment-baseline: middle !important;
    }
    
    /* Force all chart elements to be properly positioned */
    .google-visualization-container svg > g > g:nth-child(n+3) {
      transform: translateY(-30px) !important;
    }
    
    /* Target the timeline specifically */
    .google-visualization-container svg > g:first-child {
      max-height: 25px !important;
      height: 25px !important;
      margin-bottom: -25px !important;
    }
    
    /* Hide any empty space elements */
    .google-visualization-container svg > g > g:empty {
      display: none !important;
    }
    
    /* Style date row at bottom of chart */
    .google-visualization-container text[text-anchor="middle"] {
      fill: white !important;
      dominant-baseline: middle !important;
      alignment-baseline: middle !important;
    }
    
    /* Set background for date row */
    .google-visualization-container svg > g:last-child > g:first-child > rect {
      fill: #1A1F2C !important;
    }
    
    /* Set background for the entire chart */
    .google-visualization-container svg > rect:first-child {
      fill: #1A1F2C !important;
    }
    
    /* Make the task bars have better hover states for tooltips */
    .google-visualization-container rect[fill] {
      transition: filter 0.2s ease;
    }
    
    .google-visualization-container rect[fill]:hover {
      filter: brightness(1.2);
      cursor: pointer;
    }
    
    /* Improve tooltip visibility */
    .gantt-tooltip {
      opacity: 1 !important;
      visibility: visible !important;
      pointer-events: none !important;
      z-index: 10000 !important;
    }
    
    /* Remove bottom whitespace */
    .google-visualization-container svg {
      overflow: hidden !important;
    }
    
    /* Force task rows to be tight together */
    .google-visualization-container svg > g > g:nth-child(n+3) > g {
      transform: translateY(0) !important;
    }
    
    /* Hide any element that might be creating whitespace at bottom */
    .google-visualization-container svg > g:last-child > g:last-child {
      display: none !important;
    }
    
    /* Style milestone diamonds differently - Enhanced rules for better visibility */
    .google-visualization-container path[d*="M"][d*="l"][d*="Z"] {
      fill: #FF9F43 !important;
      stroke: #E6802A !important;
      stroke-width: 2px !important;
      transform: scale(1.5) !important;
    }
    
    /* Style critical path arrows differently */
    .google-visualization-container g[stroke="#e64a19"] {
      stroke-width: 3px !important;
      marker-end: url(#arrowRedMarker) !important;
    }
    
    /* Make tooltips always visible on hover */
    .google-visualization-tooltip {
      opacity: 1 !important;
      visibility: visible !important;
    }
    
    /* Fix date text alignment in timeline */
    .google-visualization-container text {
      dominant-baseline: middle !important;
    }
  `;
  document.head.appendChild(style);
  
  // Create a custom SVG marker for critical path arrows
  const svgDefs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
  const redMarker = document.createElementNS("http://www.w3.org/2000/svg", "marker");
  redMarker.setAttribute("id", "arrowRedMarker");
  redMarker.setAttribute("markerWidth", "10");
  redMarker.setAttribute("markerHeight", "10");
  redMarker.setAttribute("refX", "9");
  redMarker.setAttribute("refY", "3");
  redMarker.setAttribute("orient", "auto");
  
  const redPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
  redPath.setAttribute("d", "M0,0 L0,6 L9,3 z");
  redPath.setAttribute("fill", "#e64a19");
  
  redMarker.appendChild(redPath);
  svgDefs.appendChild(redMarker);
  
  // Find the SVG element and add the defs
  const svgElements = document.querySelectorAll('svg');
  svgElements.forEach(svg => {
    if (!svg.querySelector('#arrowRedMarker')) {
      svg.appendChild(svgDefs.cloneNode(true));
    }
  });
};

/**
 * Apply direct DOM manipulations to the chart SVG after it's rendered
 */
export const enhanceChartDOM = (
  chartContainerRef: React.RefObject<HTMLDivElement>, 
  chartHeight: number,
  selectedProject: any
): void => {
  setTimeout(() => {
    try {
      // Target SVG elements to adjust positioning
      const svgContainer = chartContainerRef.current?.querySelector('svg');
      if (svgContainer) {
        // Force SVG height to be exact - removing fixed height
        (svgContainer as SVGElement).style.overflow = 'hidden';
        (svgContainer as SVGElement).style.backgroundColor = '#1A1F2C';
        
        // Find the timeline group and reduce its height
        const timelineGroup = svgContainer.querySelector('g > g:first-child');
        if (timelineGroup) {
          (timelineGroup as SVGElement).style.height = '25px';
          (timelineGroup as SVGElement).style.overflow = 'hidden';
        }
        
        // Find all task groups and move them up
        const taskGroups = svgContainer.querySelectorAll('g > g:nth-child(n+3)');
        taskGroups.forEach((group) => {
          (group as SVGElement).style.transform = 'translateY(-30px)';
        });
        
        // Modify date text elements to be centered vertically and add years to January
        enhanceDateText(svgContainer, selectedProject);
        
        // Enhance milestone visualization
        enhanceMilestones(svgContainer);
        
        // Style critical path arrow lines
        const arrowLines = svgContainer.querySelectorAll('path');
        arrowLines.forEach(path => {
          const pathElement = path as SVGPathElement;
          
          // Critical path arrows are red
          if (pathElement.getAttribute('stroke') === '#e64a19') {
            pathElement.setAttribute('stroke-width', '3');
            // Set marker end to the custom one we defined
            pathElement.setAttribute('marker-end', 'url(#arrowRedMarker)');
          }
        });
        
        // Try to find and remove any whitespace elements
        const allGroups = svgContainer.querySelectorAll('g');
        allGroups.forEach((group) => {
          if (group.childNodes.length === 0) {
            group.style.display = 'none';
          }
        });
        
        // Remove any artifacts at the bottom that might create whitespace
        const lastChild = svgContainer.querySelector('g:last-child');
        if (lastChild) {
          const lastChildGroups = lastChild.querySelectorAll('g');
          if (lastChildGroups.length > 1) {
            lastChildGroups[lastChildGroups.length - 1].style.display = 'none';
          }
        }
        
        // Improve tooltip behavior by adding listeners to task bars
        enhanceTaskBars(svgContainer);
        
        // Set auto height on container to prevent excess space
        if (chartContainerRef.current) {
          chartContainerRef.current.style.overflow = 'hidden';
          chartContainerRef.current.style.height = 'auto';
        }
        
        // Directly target and modify any whitespace regions
        const allRects = svgContainer.querySelectorAll('rect');
        allRects.forEach((rect) => {
          const rectElement = rect as SVGRectElement;
          // If this is a background rect or a rect that might create whitespace at bottom
          if (rectElement.getAttribute('height') && 
              parseInt(rectElement.getAttribute('height') || '0') > 500 &&
              !rectElement.getAttribute('fill')) {
            rectElement.setAttribute('height', chartHeight.toString());
          }
        });
      }
    } catch (err) {
      console.error('Error while adjusting chart DOM:', err);
    }
  }, 100);
};

/**
 * Enhance the milestone diamond shapes
 */
const enhanceMilestones = (svgContainer: Element): void => {
  // Target milestone diamond shapes which are represented by path elements with specific d attribute patterns
  const milestoneShapes = Array.from(svgContainer.querySelectorAll('path'))
    .filter(path => {
      const d = path.getAttribute('d') || '';
      // Diamond shapes in Google Gantt typically have d attributes with "M" (move to) and "l" (line to) and end with "Z" (close path)
      return d.includes('M') && d.includes('l') && d.includes('Z') && d.split('l').length > 3;
    });
    
  milestoneShapes.forEach(diamond => {
    // Make milestones more visible with distinct colors
    (diamond as SVGElement).style.fill = '#FF9F43';
    (diamond as SVGElement).style.stroke = '#E6802A';
    (diamond as SVGElement).style.strokeWidth = '2px';
    // Scale up the diamond shape significantly to make it more visible
    (diamond as SVGElement).style.transform = 'scale(1.5)';
  });
};

/**
 * Enhance the date text elements to display year before January months
 */
const enhanceDateText = (svgContainer: Element, selectedProject: any): void => {
  const dateTexts = svgContainer.querySelectorAll('text[text-anchor="middle"]');
  dateTexts.forEach((text) => {
    (text as SVGElement).setAttribute('dominant-baseline', 'middle');
    (text as SVGElement).setAttribute('alignment-baseline', 'middle');
    (text as SVGElement).style.fill = 'white';
    
    // Add year to January months
    const textContent = text.textContent || '';
    if (textContent.includes('Jan')) {
      // Get all dates in the chart
      const dateValues = Array.from(dateTexts).map(t => t.textContent || '');
      // If this is the first Jan in the timeline or if preceded by Dec, add the year
      const prevMonthIndex = dateValues.indexOf(textContent) - 1;
      if (prevMonthIndex >= 0 && dateValues[prevMonthIndex].includes('Dec') || dateValues.indexOf(textContent) === 0) {
        // Get the closest year from a task date
        let yearToShow = '';
        // Find the closest task to this month to get the year
        for (let i = 0; i < selectedProject.tasks.length; i++) {
          const task = selectedProject.tasks[i];
          const startDate = task.startDate ? new Date(task.startDate) : null;
          if (startDate && startDate.getMonth() === 0) { // January is month 0
            yearToShow = startDate.getFullYear().toString();
            break;
          }
        }
        // If we couldn't find a task in January, use the project start date year
        if (!yearToShow) {
          const projectDate = new Date(selectedProject.startDate || Date.now());
          yearToShow = projectDate.getFullYear().toString();
        }
        // Update the text content to include year
        text.textContent = `Jan '${yearToShow.substring(2)}`; // Format: Jan '23
      }
    }
  });
};

/**
 * Enhance task bars with hover effects
 */
const enhanceTaskBars = (svgContainer: Element): void => {
  const taskBars = svgContainer.querySelectorAll('rect[fill]:not([fill="none"])');
  taskBars.forEach((bar) => {
    // Force tooltips to always show on hover
    bar.addEventListener('mouseenter', () => {
      (bar as SVGElement).style.filter = 'brightness(1.2)';
      // Force tooltip visibility
      const tooltip = document.querySelector('.google-visualization-tooltip');
      if (tooltip) {
        (tooltip as HTMLElement).style.opacity = '1';
        (tooltip as HTMLElement).style.visibility = 'visible';
        (tooltip as HTMLElement).style.display = 'block';
      }
    });
    
    bar.addEventListener('mouseleave', () => {
      (bar as SVGElement).style.filter = '';
    });
  });
};
